import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  Alert,
  Snackbar,
  DialogActions,
} from "@mui/material"
import { useNavigate } from "react-router-dom"
import { useForm, Controller, FormProvider } from "react-hook-form"

import MuiAlert from "@mui/material/Alert"
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
} from "@mui/icons-material"
import axios from "axios"
import { fetchToken } from "../../../../Auth"
import { adminURL } from "../../../../global"
export default function NewsSection({ privileges }) {
  const { createPrivilege, deletePrivilege, editPrivilege, livePrivilege } = privileges

  // Initialize React Hook Form
  const formMethods = useForm()
  const { control, handleSubmit, reset, setValue, getValues } = formMethods
  const [originalImagePreviews, setOriginalImagePreviews] = useState({})
  const [newsItems, setNewsItems] = useState([])
  // Store the original state of news items before editing
  const [originalNewsItems, setOriginalNewsItems] = useState([])
  // Track newly created items that haven't been saved
  const [newItemIds, setNewItemIds] = useState([])
  // Track the current mode: 'view', 'create', or 'edit'
  const [mode, setMode] = useState("view")
  // Track items marked for deletion
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingChange, setPendingChange] = useState(null)
  const navigate = useNavigate()
  const [pendingDeletions, setPendingDeletions] = useState([])
  const [toastMessage, setToastMessage] = useState({
    open: false,
    message: "",
    severity: "info",
  })

  const [maxId, setMaxId] = useState(1)
  const [isEditMode, setIsEditMode] = useState(false)
  const [expandedItems, setExpandedItems] = useState({})
  const [imagePreview, setImagePreview] = useState({})
  const [defaultNews, setDefaultNews] = useState([])
  // Modify the state to store the actual file objects
  const [selectedFiles, setSelectedFiles] = useState({})
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    itemId: null,
  })
  const [errors, setErrors] = useState({})
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  // When entering edit mode, store the original state
  const getNews = async () => {
    try {
      const response = await axios.get(`/backend/get_news/0`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })
      setDefaultNews(response.data || [])
      setNewsItems(response.data || [])

      // Initialize form values for each news item
      const formValues = {}
      response.data.forEach((item) => {
        formValues[`title-${item.id}`] = item.title || ""
        formValues[`publishDate-${item.id}`] = item.publishDate || ""
        formValues[`url-${item.id}`] = item.url || ""
      })
      reset(formValues)
    } catch (err) {
      console.error("Error", err)
      if (err.response.status === 401) {
        navigate(`/niyantran-panel/${adminURL}/login`)
      }
    }
  }

  useEffect(() => {
    getNews()
    fetchMaxId()
  }, [])

  useEffect(() => {
    if (isEditMode) {
      // Create a deep copy of the news items to ensure nested objects are also copied
      setOriginalNewsItems(newsItems)
      setOriginalImagePreviews({ ...imagePreview })
      // Initialize form values when entering edit mode
      const formValues = {}
      newsItems.forEach((item) => {
        formValues[`title-${item.id}`] = item.title || ""
        formValues[`publishDate-${item.id}`] = item.publishDate || ""
        formValues[`url-${item.id}`] = item.url || ""
      })
      reset(formValues)
    } else {
      // Clear tracking of new items when exiting edit mode
      setNewItemIds([])
      // Clear pending deletions when exiting edit mode
      setPendingDeletions([])
    }
  }, [isEditMode, reset])

  // Add a new useEffect to fetch images when newsData is loaded
  // Add this after the existing useEffect that initializes maxId
  const fetchNewsImage = async (item) => {
    try {
      const itemId = item.id
      // Find the news item to get its image type
      const imgId = `img-${itemId}`

      // Get the file extension from the image type
      let fileExtension = "jpg" // Default extension
      if (item && item[imgId]?.type) {
        const imageType = item[imgId].type
        if (imageType === "image/jpeg" || imageType === "image/jpg") {
          fileExtension = "jpeg"
        } else if (imageType === "image/png") {
          fileExtension = "png"
        } else if (imageType === "image/gif") {
          fileExtension = "gif"
        }
      }
      const imageID = `news-${itemId.toString()}-${imgId}.${fileExtension}`
      // Updated URL format to include the file extension
      const response = await axios.get(`/backend/get_news_image/${imageID}`, {
        headers: {
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })
      return response.data // This should be the presigned URL
    } catch (error) {
      console.error(`Error fetching image for news item ${item}:`, error)
      return null
    }
  }

  // Add a function to fetch the max ID from the API

  // useEffect(() => {
  //   let isMounted = true; // Flag to prevent state update if unmounted

  //   const fetchMaxId = async () => {
  //     try {
  //       const response = await axios.get("/get_max_id", {
  //         headers: {
  //           Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
  //         },
  //       });

  //       if (response.status === 200 && isMounted) {
  //         setMaxId(response.data.nextId);
  //       } else {
  //         console.error("Failed to get max ID:", response.data.msg);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching max ID:", error);
  //     }
  //   };

  //   fetchMaxId();

  //   return () => {
  //     isMounted = false; // Prevent state updates if component unmounts
  //   };
  // }, []);
  const fetchMaxId = async () => {
    try {
      const response = await axios.get("/backend/get_max_id", {
        headers: {
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })

      if (response.status === 200) {
        setMaxId(response.data.nextId)
      } else {
        console.error("Failed to get max ID:", response.data.msg)
      }
    } catch (error) {
      console.error("Error fetching max ID:", error)
    }
  }
  // useEffect(() => {
  //   let isMounted = true; // Flag to prevent state update if unmounted

  //   fetchMaxId();

  //   return () => {
  //     isMounted = false; // Prevent state updates if component unmounts
  //   };
  // }, [idChanged]);

  useEffect(() => {
    if (newsItems && newsItems.length > 0) {
      // Initialize image previews for all items
      const initialPreviews = {}
      newsItems.forEach((item) => {
        initialPreviews[item.id] = "/placeholder.svg?height=200&width=300"
      })
      setImagePreview((prev) => ({ ...prev, ...initialPreviews }))

      // Fetch actual images for each news item
      const fetchImages = async () => {
        for (const item of newsItems) {
          const imgId = `img-${item.id}`
          if (item[imgId]?.type) {
            const imageUrl = await fetchNewsImage(item)
            if (imageUrl) {
              setImagePreview((prev) => ({ ...prev, [item.id]: imageUrl }))
            }
          }
        }
      }
      // console.log("____________________________________");
      fetchImages()
    }
  }, [defaultNews])

  // useEffect(() => {
  //   if (newsItems && newsItems.length > 0) {
  //     const sortedItems = [...newsItems].sort((a, b) => {
  //       // Items with sequence -1 should be at the end
  //       if (a.sequence === -1 && b.sequence !== -1) {
  //         return 1
  //       }
  //       if (a.sequence !== -1 && b.sequence === -1) {
  //         return -1
  //       }

  //       // For items with valid sequences, sort by sequence
  //       if (a.sequence !== -1 && b.sequence !== -1) {
  //         return a.sequence - b.sequence
  //       }

  //       // If both have sequence -1, sort by ID (most recent first)
  //       return b.id - a.id
  //     })
  //     console.log("______________________+++++++++++++++++++++++==")
  //     setNewsItems(sortedItems)
  //   }
  // }, [defaultNews])

  // Check if a news item is complete with all required fields
  const isItemComplete = (item) => {
    const imgId = `img-${item.id}`
    return Boolean(
      item.title &&
        item.publishDate &&
        item.url &&
        (item.url.startsWith("www.") || item.url.startsWith("https://")) &&
        item[imgId]?.type,
    )
  }

  // Check if the last item is complete before allowing a new one
  // const isLastItemComplete = () => {
  //   if (newsItems.length === 0) return true;
  //   // Check if the first item is complete when in edit mode
  //   const firstItem = newsItems[0];
  //   if (isEditMode && firstItem && newItemIds.includes(firstItem.id)) {
  //     return isItemComplete(firstItem);
  //   }
  //   return true;
  // };

  // Function to check if an item is editable based on the current mode
  const isItemEditable = (itemId) => {
    if (mode === "create") {
      // Only the newly created item is editable in create mode
      return newItemIds.includes(itemId)
    }
    // All items are editable in edit mode
    return mode === "edit"
  }

  // Function to check if sequence controls should be enabled
  const isSequenceEnabled = () => {
    // Sequence controls are disabled in create mode
    return mode === "edit"
  }

  const handleAddNewsItem = async () => {
    // Set mode to 'create' when adding a new item
    setMode("create")
    setIsEditMode(true)

    try {
      // Fetch the next ID from the API
      const nextId = maxId + 1

      if (!nextId) {
        alert("Failed to get the next ID. Please try again.")
        setMode("view")
        setIsEditMode(false)
        return
      }

      const imgId = `img-${nextId}`

      // Add the new item at the beginning of the array
      setNewsItems([
        {
          id: nextId,
          title: "",
          publishDate: "",
          url: "",
          isLive: false,
          sequence: -1,
          [imgId]: {
            type: null,
          },
        },
        ...newsItems,
      ])

      // Initialize form values for the new item
      setValue(`title-${nextId}`, "")
      setValue(`publishDate-${nextId}`, "")
      setValue(`url-${nextId}`, "")

      // Track only this new item
      setNewItemIds([nextId])

      // Expand only the new item
      setExpandedItems({
        [nextId]: true,
      })
    } catch (error) {
      console.error("Error adding new news item:", error)
      alert("Failed to add new news item. Please try again.")
      setMode("view")
      setIsEditMode(false)
    }
  }

  const handleNewsItemChange = (itemId, field, value) => {
    // Update the form value
    setValue(`${field}-${itemId}`, value)

    // Update the news items state
    setNewsItems((prevNewsItems) => {
      const maxLiveSequence = Math.max(0, ...prevNewsItems.filter((i) => i.isLive).map((i) => i.sequence))

      return prevNewsItems.map((item) =>
        item.id === itemId
          ? {
              ...item,
              [field]: value,
              ...(field === "isLive" && {
                sequence: value ? maxLiveSequence + 1 : -1,
              }),
            }
          : item,
      )
    })

    // Clear error for this field if validation was attempted
    if (hasAttemptedSave && errors[itemId]?.[field]) {
      setErrors((prev) => ({
        ...prev,
        [itemId]: { ...(prev[itemId] || {}), [field]: undefined },
      }))
    }
  }

  // Handle live status change directly from accordion summary
  const handleLiveStatusChange = async (itemId, isLive, e) => {
    e.stopPropagation()

    try {
      const response = await axios.post(
        "/backend/change_news_live",
        {
          id: itemId,
          flag: isLive ? 1 : 0,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
          },
        },
      )

      if (response.status === 200) {
        setToastMessage({
          open: true,
          message: "News live status updated successfully!",
          severity: "success",
        })
        resetState()
      }
    } catch (error) {
      console.error("Error updating news live status:", error)
      setToastMessage({
        open: true,
        message: error.response?.data?.msg || "Failed to update news live status",
        severity: "error",
      })
    }
  }
  // Update handleImageChange to store the file instead of uploading immediately
  const handleImageChange = (itemId, e) => {
    const file = e.target.files[0]
    if (file) {
      const validImageTypes = ["image/jpeg", "image/jpg", "image/png" , "image/webp"]
    
      if (!validImageTypes.includes(file.type)) {
        // Clear the file input
        setToastMessage({
          open: true,
          message:  "Invalid file format. Please upload only JPG, PNG,  or WebP images.",
          severity: "error",
        })
        e.target.value = ""
        return
      }
      const imgId = `img-${itemId}`

      // Store the file for later upload
      setSelectedFiles((prev) => ({
        ...prev,
        [itemId]: file,
      }))

      // Update the newsItems to include the image data
      setNewsItems((prevNewsItems) =>
        prevNewsItems.map((item) =>
          item.id === itemId
            ? {
                ...item,
                [imgId]: { type: file.type },
              }
            : item,
        ),
      )

      // Create preview
      const reader = new FileReader()
      reader.onload = (event) => {
        setImagePreview({ ...imagePreview, [itemId]: event.target.result })
      }
      reader.readAsDataURL(file)

      // Clear error for image if validation was attempted
      if (hasAttemptedSave && errors[itemId]?.image) {
        setErrors((prev) => ({
          ...prev,
          [itemId]: { ...(prev[itemId] || {}), image: undefined },
        }))
      }
    }
  }

  const handleMoveItem = (index, direction) => {
    // Only allow sequence changes in edit mode
    if (!isSequenceEnabled()) return

    const newItems = [...newsItems]
    const item = newItems.splice(index, 1)[0]
    newItems.splice(index + direction, 0, item)

    // Update sequences only for live items
    const updatedItems = newItems.map((item, idx) => ({
      ...item,
      sequence: item.isLive
        ? // Find all live items and assign sequential numbers
          newItems.filter((i) => i.isLive).findIndex((i) => i.id === item.id) + 1
        : -1,
    }))

    setNewsItems(updatedItems)
  }

  // Modified to mark item for deletion instead of deleting immediately
  const handleMarkItemForDeletion = (itemId) => {
    // For newly created items that haven't been saved yet, just remove them
    if (newItemIds.includes(itemId)) {
      setNewsItems((prevNewsItems) => prevNewsItems.filter((item) => item.id !== itemId))

      // Clean up the image preview
      const newImagePreview = { ...imagePreview }
      delete newImagePreview[itemId]
      setImagePreview(newImagePreview)
    } else {
      // Add existing items to the pending deletions list
      setPendingDeletions((prev) => [...prev, itemId])
    }

    setDeleteDialog({ open: false, itemId: null })
  }

  // Add this function to handle API deletion - now only called during save
  const handleDeleteNews = async (itemIds) => {
    try {
      // Find the news item by ID

      // if (!itemToDelete) {
      //   console.error(`News item with ID ${itemId} not found`);
      //   return;
      // }

      // Prepare the payload as required by the backend
      //imageId
      const payload = {
        ids: itemIds,
      }

      // Call the delete API
      const response = await axios.post("/backend/delete_news", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })

      // Return the result so the save function can track successful deletions
      return response.data
    } catch (error) {
      console.error(`Error deleting news items:`, error)
      throw new Error(error.response?.data?.msg || `Failed to delete news items `)
    }
  }

  // Update the handleDeleteConfirm function to mark for deletion instead of immediate deletion
  const handleDeleteConfirm = () => {
    handleMarkItemForDeletion(deleteDialog.itemId)
  }

  // Update the handleEdit function to ensure form values are properly initialized
  const handleEdit = () => {
    if (editPrivilege) {
      setMode("edit")
      setIsEditMode(true)
      // Store the original state when entering edit mode
      setOriginalNewsItems([...newsItems])

      // Initialize form values for all items
      const formValues = {}
      newsItems.forEach((item) => {
        formValues[`title-${item.id}`] = item.title || ""
        formValues[`publishDate-${item.id}`] = item.publishDate || ""
        formValues[`url-${item.id}`] = item.url || ""
      })
      reset(formValues)

      // Clear any pending deletions
      setPendingDeletions([])
    }
  }

  // Custom function to handle accordion expansion/collapse
  const handleAccordionChange = (itemId) => {
    // In create mode, if trying to collapse the new item, check if it's complete
    if (mode === "create" && newItemIds.includes(itemId) && expandedItems[itemId]) {
      const item = newsItems.find((item) => item.id === itemId)

      // If the item is not complete, prevent collapsing
      if (item && !isItemComplete(item)) {
        alert("Please complete all required fields before collapsing this item.")
        return
      }
    }

    // Toggle the expanded state
    toggleExpand(itemId)
  }
  const resetState = async () => {
    setMode("view")
    setIsEditMode(false)
    setHasAttemptedSave(false)
    setNewItemIds([])
    setSelectedFiles({})
    setPendingDeletions([])
    setExpandedItems({})
    setNewsItems([])
    setDefaultNews([])

    // Reset form
    reset({})

    fetchMaxId()
    getNews()
  }

  // Update the onSubmit function to properly handle form data
  const onSubmit = async (formData) => {
    setHasAttemptedSave(true)
    const validationErrors = {}
    let isValid = true
  
    // In create mode, only validate the new item
    const itemsToValidate =
      mode === "create"
        ? newsItems.filter((item) => newItemIds.includes(item.id))
        : newsItems.filter((item) => !pendingDeletions.includes(item.id))
  
    itemsToValidate.forEach((item) => {
      const itemErrors = {}
      const trimmedFormData = { ...formData };
      const imgId = `img-${item.id}`
  
      // Get values from form data
      const titleKey = `title-${item.id}`
      const publishDate = formData[`publishDate-${item.id}`]
      const url = formData[`url-${item.id}`]
      if (formData[titleKey]) {
        trimmedFormData[titleKey] = formData[titleKey].trim()
        setValue(titleKey, trimmedFormData[titleKey])
      }
      const title = trimmedFormData[titleKey]
      
      if (!title) {
        itemErrors.title = "Title is required"
        isValid = false
      }else if (title.length > 150) {
        itemErrors.title = "Title must be 150 characters or less"
        isValid = false
      }
      if (!publishDate) {
        itemErrors.publishDate = "Publish Date is required"
        isValid = false
      }
      if (!url) {
        itemErrors.url = "URL is required"
        isValid = false
      } else if (!url.startsWith("www.") && !url.startsWith("https://")) {
        itemErrors.url = "URL must start with 'www.' or 'https://'"
        isValid = false
      }
      if (!item[imgId]?.type) {
        itemErrors.image = "Image is required"
        isValid = false
      }
      if (Object.keys(itemErrors).length > 0) {
        validationErrors[item.id] = itemErrors
      }
    })
  
    setErrors(validationErrors)
  
    if (!isValid) {
      // Prepare toast message for incomplete fields
      const incompleteItems = Object.keys(validationErrors).map((itemId) => {
        const item = newsItems.find((item) => item.id.toString() === itemId)
        const itemTitle = item && formData[`title-${item.id}`] ? formData[`title-${item.id}`] : ``
        const missingFields = Object.keys(validationErrors[itemId]).join(", ")
        return `${itemTitle}: Missing ${missingFields}`
      })
  
      // Show toast with all incomplete items
      const toastMessage = incompleteItems.join("; ")
      setToastMessage({
        open: true,
        message: toastMessage,
        severity: "error",
      })
      return
    }
  
    setIsSaving(true)
  
    try {
      // Update items with form values from React Hook Form
      const updatedNewsItems = newsItems.map((item) => ({
        ...item,
        title: formData[`title-${item.id}`] || "",
        publishDate: formData[`publishDate-${item.id}`] || "",
        url: formData[`url-${item.id}`] || "",
      }))
  
      // Process pending deletions first
      if (pendingDeletions.length > 0) {
        try {
          await handleDeleteNews(pendingDeletions)
        } catch (error) {
          console.error(`Failed to delete items:`, error)
          throw new Error(`Failed to delete items: ${error.message}`)
        }
      }
  
      // Update items with form values
      const updatedNewsItems2 = newsItems.map((item) => ({
        ...item,
        title: formData[`title-${item.id}`] || "",
        publishDate: formData[`publishDate-${item.id}`] || "",
        url: formData[`url-${item.id}`] || "",
      }))

  
      // Then create or update the news items based on mode
      if (mode === "create") {
        // Only create the new items - use sequential processing
        for (const itemId of newItemIds) {
          const item = updatedNewsItems2.find((item) => item.id === itemId)
          if (!item) continue
  
          const imgId = `img-${itemId}`
  
          // First create the news item without image
          const newsItemData = {
            title: item.title,
            publishDate: item.publishDate,
            url: item.url,
            isLive: item.isLive,
            sequence: item.sequence,
            imgKey: item[imgId],
          }
  
          try {
            // First call the create_news API to get the ID
            const createResponse = await axios.post("/backend/create_news", newsItemData, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
              },
            })
  
            // Get the ID from the response
            const responseId = createResponse.data.id
  
            // Now upload image if exists using the ID from the response
            if (selectedFiles[itemId]) {
              const formData = new FormData()
              const imgId = `img-${responseId}` // Use responseId instead of itemId
  
              const fileType = selectedFiles[itemId].type
              const fileExtension =
                fileType === "image/jpeg"
                  ? "jpeg"
                  : fileType === "image/jpg"
                    ? "jpg"
                    : fileType === "image/png"
                      ? "png"
                      : "jpg"
              const imageID = `news-${responseId.toString()}-${imgId}.${fileExtension}`
              formData.append("img_id", `${imageID}`)
              formData.append("file", selectedFiles[itemId])
  
              await axios.post("/backend/upload_news_images", formData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
                },
              })
            }
          } catch (error) {
            console.error("Error creating news item:", error)
            throw new Error(error.response?.data?.msg || "Failed to create news item")
          }
        }
      } else if (mode === "edit") {
        // For edit mode, keep the parallel processing approach
        // Filter out pending deletions when handling updates
        const itemsToUpdate = updatedNewsItems2
          .filter((item) => !newItemIds.includes(item.id))
          .filter((item) => !pendingDeletions.includes(item.id))
  
        // First, upload any new images for existing items - using parallel processing
        const imageUploadPromises = Object.keys(selectedFiles)
          .filter((itemId) => !newItemIds.includes(Number.parseInt(itemId)))
          .map(async (itemId) => {
            const numericId = Number.parseInt(itemId)
            if (selectedFiles[numericId]) {
              const formData = new FormData()
              const imgId = `img-${itemId}`
              const fileType = selectedFiles[numericId].type
              const fileExtension =
                fileType === "image/jpeg"
                  ? "jpeg"
                  : fileType === "image/jpg"
                    ? "jpg"
                    : fileType === "image/png"
                      ? "png"
                      : "jpg"
              const imageID = `news-${itemId.toString()}-${imgId}.${fileExtension}`
              formData.append("img_id", `${imageID}`)
              formData.append("file", selectedFiles[numericId])
  
              try {
                const response = await axios.post("/backend/upload_news_images", formData, {
                  headers: {
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
                  },
                })
                return response.data
              } catch (error) {
                console.error(`Error uploading image for item ${numericId}:`, error)
                throw new Error(`Failed to upload image for item ${numericId}: ${error.message}`)
              }
            }
            return null
          })
  
        // Wait for all image uploads to complete
        await Promise.all(imageUploadPromises)
  
        // Prepare an array of edited items to send in a single API call
        const editedItems = []
  
        // Process each item to check if it's been edited
        for (const item of itemsToUpdate) {
          // Find the original item to compare changes
          const originalItem = originalNewsItems.find((orig) => orig.id === item.id)
          const imgId = `img-${item.id}`
  
          // Check if any field has been edited
          let isEdited = false
  
          if (originalItem) {
            // Compare each field individually
            const titleChanged = item.title !== originalItem.title
            const dateChanged = item.publishDate !== originalItem.publishDate
            const urlChanged = item.url !== originalItem.url
            const imageMetaChanged = JSON.stringify(item[imgId]) !== JSON.stringify(originalItem[imgId])
            const hasNewImage = Boolean(selectedFiles[item.id])
  
            isEdited = titleChanged || dateChanged || urlChanged ||  imageMetaChanged || hasNewImage
          }
  
          // Skip items that haven't been edited
          if (!isEdited) {
            continue
          }
  
          // Prepare the payload for the edited item
          const newsItemData = {
            id: item.id,
            title: item.title,
            publishDate: item.publishDate,
            url: item.url,
            isLive: item.isLive,
            [imgId]: item[imgId],
            isEdited: isEdited,
          }
  
          // Add to the array of edited items
          editedItems.push(newsItemData)
        }
  
        // If there are edited items, send them in a batch
        if (editedItems.length > 0) {
          try {
            // Call the edit_news API with an array of items
            const response = await axios.post("/backend/edit_news", editedItems, {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
              },
            })
          } catch (error) {
            console.error("Error updating news items:", error)
            // If the error is about URL already existing, show a specific message
            if (error.response?.data?.msg?.includes("URL already exists")) {
              throw new Error(`One of the URLs is already used by another news item`)
            }
            throw new Error(error.response?.data?.msg || `Failed to update news items`)
          }
        }
      }
  
      setToastMessage({
        open: true,
        message: "News items saved successfully!",
        severity: "success",
      })
      resetState()
    } catch (error) {
      console.error("Error saving news items:", error)
      setToastMessage({
        open: true,
        message: `Failed to save news items: ${error.message}`,
        severity: "error",
      })
    } finally {
      setIsSaving(false)
    }
  }
  const handleCancel = () => {
    setShowCancelDialog(true)
  }

  const handleCancelConfirm = () => {
    // If we have new items that haven't been saved, remove them
    if (newItemIds.length > 0) {
      setNewsItems((prevNewsItems) => prevNewsItems.filter((item) => !newItemIds.includes(item.id)))

      // Clean up image previews for unsaved items
      const newImagePreview = { ...imagePreview }
      newItemIds.forEach((id) => {
        delete newImagePreview[id]
      })
      setImagePreview(newImagePreview)
    } else {
      // If no new items, revert to the original state
      setNewsItems(originalNewsItems)
      setImagePreview(originalImagePreviews)
    }

    // Reset state
    setMode("view")
    setIsEditMode(false)
    setHasAttemptedSave(false)
    setErrors({})
    setNewItemIds([])
    setSelectedFiles({}) // Clear selected files on cancel
    setPendingDeletions([]) // Clear pending deletions on cancel
    setShowCancelDialog(false)

    // Reset form to original values
    reset()
  }

  const handleCancelDialogClose = () => {
    setShowCancelDialog(false)
  }

  const toggleExpand = (itemId) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }))
  }

  // Helper function to check if an item is pending deletion
  const isItemPendingDeletion = (itemId) => {
    return pendingDeletions.includes(itemId)
  }

  return (
    <FormProvider {...formMethods}>
      <Box sx={{ width: "100%", maxWidth: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom>
              News & Media
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage your news items and media content
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {isEditMode ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit(onSubmit)}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : mode === "create" ? "Create News" : "Save Changes"}
                </Button>
                <Button variant="outlined" color="warning" onClick={handleCancel}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                {editPrivilege && (
                  <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
                    Edit
                  </Button>
                )}
              </>
            )}

            {/* Add News button is conditionally visible based on mode */}
            {createPrivilege && mode !== "edit" && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNewsItem}
                disabled={mode === "create"}
              >
                Add News
              </Button>
            )}
          </Box>
        </Box>

        {/* Mode indicator */}
        {isEditMode && (
          <Box sx={{ mb: 3 }}>
            <Alert
              severity="info"
              icon={mode === "create" ? <AddIcon /> : <EditIcon />}
              action={
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "4px",
                    backgroundColor: mode === "create" ? "rgba(25, 118, 210, 0.12)" : "rgba(156, 39, 176, 0.12)",
                    color: mode === "create" ? "primary.main" : "secondary.main",
                    border: "1px solid",
                    borderColor: mode === "create" ? "primary.light" : "secondary.light",
                    typography: "caption",
                    fontWeight: "medium",
                  }}
                >
                  {mode === "create" ? "Create Mode" : "Edit Mode"}
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      ml: 1,
                      backgroundColor: mode === "create" ? "primary.main" : "secondary.main",
                    }}
                  />
                </Box>
              }
            >
              {mode === "create"
                ? "You are creating a new news item. Other items cannot be edited at this time."
                : "You are in edit mode. You can modify all news items."}
            </Alert>
          </Box>
        )}

        {/* Show a summary of pending deletions if any */}
        {pendingDeletions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="warning">
              {pendingDeletions.length} {pendingDeletions.length === 1 ? "item" : "items"} marked for deletion. Changes
              will be applied when you click "Save Changes".
            </Alert>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {newsItems.length != 0 &&
            newsItems.map((item, index) => {
              const isPendingDeletion = isItemPendingDeletion(item.id)
              const isNewItem = newItemIds.includes(item.id)
              const isGreyedOut = mode === "create" && !isNewItem

              return (
                <Accordion
                  key={item.id}
                  expanded={Boolean(expandedItems[item.id])}
                  onChange={() => handleAccordionChange(item.id)}
                  sx={{
                    mb: 2,
                    backgroundColor: isPendingDeletion
                      ? "rgba(255, 0, 0, 0.05)"
                      : isGreyedOut
                        ? "rgba(0, 0, 0, 0.05)"
                        : "white",
                    boxShadow: 1,
                    borderRadius: 2,
                    opacity: isPendingDeletion || isGreyedOut ? 0.7 : 1,
                    position: "relative",
                  }}
                  disabled={isGreyedOut}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      "& .MuiAccordionSummary-content": {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flex: 1,
                      }}
                    >
                      <Typography variant="h6">
                        {item.title || "New Item"}{" "}
                      </Typography>

                      {/* Live Status in summary */}
                      {livePrivilege && mode !== "create" && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            ml: "auto",
                            mr: 2,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            Status:
                          </Typography>
                          <Switch
                            size="small"
                            checked={Boolean(item.isLive)}
                            onChange={(e) => {
                              e.stopPropagation()
                              setPendingChange({
                                itemId: item.id,
                                isLive: e.target.checked,
                                event: e,
                              })
                              setDialogOpen(true)
                            }}
                            disabled={!livePrivilege || isPendingDeletion || mode === "edit" || mode === "create"}
                            color="primary"
                          />
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                            {item.isLive ? "Published" : "Draft"}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      {/* <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMoveItem(index, -1)
                      }}
                      disabled={index === 0 || !isEditMode || !isSequenceEnabled() || isPendingDeletion || isGreyedOut}
                    >
                      <ArrowUpwardIcon />
                    </IconButton> */}
                      {/* <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMoveItem(index, 1)
                      }}
                      disabled={
                        index === newsItems.length - 1 ||
                        !isEditMode ||
                        !isSequenceEnabled() ||
                        isPendingDeletion ||
                        isGreyedOut
                      }
                    >
                      <ArrowDownwardIcon />
                    </IconButton> */}
                      {/* Hide delete icon completely in create mode */}
                      {mode !== "create" && (
                        <IconButton
                          color={isPendingDeletion ? "default" : "error"}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (!isPendingDeletion) {
                              setDeleteDialog({ open: true, itemId: item.id })
                            }
                          }}
                          disabled={!isEditMode || !deletePrivilege || isPendingDeletion || isGreyedOut}
                          sx={{ display: deletePrivilege ? "flex" : "none" }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <Controller
                        name={`title-${item.id}`}
                        control={control}
                        defaultValue={item.title || ""}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            required
                            label="Title"
                            disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                            error={hasAttemptedSave && Boolean(errors[item.id]?.title)}
                            helperText={hasAttemptedSave && errors[item.id]?.title}
                          />
                        )}
                      />

                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Controller
                          name={`publishDate-${item.id}`}
                          control={control}
                          defaultValue={item.publishDate || ""}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              sx={{ flex: 1 }}
                              required
                              label="Publish Date"
                              type="date"
                              disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                              InputLabelProps={{ shrink: true }}
                              error={hasAttemptedSave && Boolean(errors[item.id]?.publishDate)}
                              helperText={hasAttemptedSave && errors[item.id]?.publishDate}
                            />
                          )}
                        />
                        <Controller
                          name={`url-${item.id}`}
                          control={control}
                          defaultValue={item.url || ""}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              sx={{ flex: 2 }}
                              required
                              label="URL must start with 'www.' or 'https://"
                              disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                              error={hasAttemptedSave && Boolean(errors[item.id]?.url)}
                              helperText={
                                (hasAttemptedSave && errors[item.id]?.url) || "URL must start with 'www.' or 'https://"
                              }
                              placeholder="https:// or 'www."
                            />
                          )}
                        />
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<ImageIcon />}
                            disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                          >
                            Upload Image
                            <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                              *
                            </Typography>
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => handleImageChange(item.id, e)}
                              disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                            />
                          </Button>
                          {imagePreview[item.id] && (
                            <Typography variant="body2" color="text.secondary">
                              Image selected
                            </Typography>
                          )}
                        </Box>
                        {hasAttemptedSave && errors[item.id]?.image && (
                          <Typography color="error" variant="caption">
                            {errors[item.id].image}
                          </Typography>
                        )}
                        {imagePreview[item.id] && (
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: "300px",
                              height: "200px",
                              position: "relative",
                              overflow: "hidden",
                              borderRadius: 1,
                            }}
                          >
                            <img
                              src={imagePreview[item.id] || "/placeholder.svg"}
                              alt="Preview"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                opacity: isPendingDeletion ? 0.5 : 1,
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )
            })}
        </Box>

        {/* Delete Dialog */}
        <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, itemId: null })}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              {mode === "edit"
                ? "This item will be marked for deletion and will be deleted when you save changes. Proceed?"
                : "Are you sure you want to delete this news item?"}
            </Typography>
          </DialogContent>
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
            <Button onClick={() => setDeleteDialog({ open: false, itemId: null })}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" disabled={isSaving}>
              {mode === "edit" ? "Mark for Deletion" : "Delete"}
            </Button>
          </Box>
        </Dialog>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} aria-labelledby="live-status-dialog-title">
          <DialogTitle id="live-status-dialog-title">Confirm Live Status Change</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to {pendingChange?.isLive ? "publish" : "unpublish"} this news item?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (pendingChange) {
                  handleLiveStatusChange(pendingChange.itemId, pendingChange.isLive, pendingChange.event)
                }
                setDialogOpen(false)
                setPendingChange(null)
              }}
              color="primary"
              variant="contained"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
        {/* Cancel Dialog */}
        <Dialog open={showCancelDialog} onClose={handleCancelDialogClose}>
          <DialogTitle>Cancel Changes</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to cancel your changes? Unsaved changes will be lost.</Typography>
            {pendingDeletions.length > 0 && (
              <Typography sx={{ mt: 1, color: "warning.main" }}>
                {pendingDeletions.length} {pendingDeletions.length === 1 ? "item" : "items"} marked for deletion will be
                restored.
              </Typography>
            )}
          </DialogContent>
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
            <Button onClick={handleCancelDialogClose}>Cancel</Button>
            <Button onClick={handleCancelConfirm}>Confirm</Button>
          </Box>
        </Dialog>

        {/* Toast Message */}
        <Snackbar
          open={toastMessage.open}
          autoHideDuration={6000}
          onClose={() => setToastMessage((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={toastMessage.severity}
            onClose={() => setToastMessage((prev) => ({ ...prev, open: false }))}
          >
            {toastMessage.message}
          </MuiAlert>
        </Snackbar>
      </Box>
  </FormProvider>
  )
}
